import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import apiService from "../services/api";
import { motion } from "framer-motion";
import { useNotifications } from "../contexts/NotificationContext";
import { Loader2, X } from "lucide-react";
import { LoadingSpinner } from "../components/ui/loading-spinner";

const IA = () => {
  const { addToastNotification } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [iaLigada, setIaLigada] = useState(false);
  const [nomePersona, setNomePersona] = useState("");
  const [descricaoPersona, setDescricaoPersona] = useState("");
  const [scriptRecepcao, setScriptRecepcao] = useState("");
  const [imagemPersona, setImagemPersona] = useState("");
  const [imagemPreview, setImagemPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isClosingModal, setIsClosingModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [cacheClearStatus, setCacheClearStatus] = useState({ phase: 'idle', message: '' });
  const [countdown, setCountdown] = useState(3);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pendingNavRef = useRef(null);
  const isDirtyRef = useRef(false);

  // Mantém ref sincronizada para uso nos listeners
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Intercepta cliques em links internos
  useEffect(() => {
    const handleClick = (e) => {
      if (!isDirtyRef.current) return;
      const anchor = e.target.closest("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("http") || href.startsWith("mailto")) return;
      if (href === location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      pendingNavRef.current = href;
      setShowUnsavedModal(true);
    };
    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [location.pathname]);

  // Intercepta botão Voltar/Avançar do browser
  useEffect(() => {
    const handlePopState = () => {
      if (!isDirtyRef.current) return;
      window.history.pushState(null, "", location.pathname);
      setShowUnsavedModal(true);
      pendingNavRef.current = null;
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [location.pathname]);

  // Bloqueia fechamento/reload da aba
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleConfirmLeave = () => {
    setIsDirty(false);
    isDirtyRef.current = false;
    setShowUnsavedModal(false);
    if (pendingNavRef.current) {
      navigate(pendingNavRef.current);
    } else {
      navigate(-1);
    }
    pendingNavRef.current = null;
  };

  const handleCancelLeave = () => {
    setShowUnsavedModal(false);
    pendingNavRef.current = null;
  };

  const carregarDados = useCallback(async () => {
    try {
      const statusResponse = await apiService.request('/ia/status', { noCache: true });
      setIaLigada(statusResponse.status);

      const settingsResponse = await apiService.request('/ia/settings', { noCache: true });
      const settings = settingsResponse.settings;

      if (settings.nome) {
        setNomePersona(settings.nome.value || "");
      }
      if (settings.descricao) {
        setDescricaoPersona(settings.descricao.value || "");
      }
      if (settings.script_recepcao) {
        setScriptRecepcao(settings.script_recepcao.value || "");
      }
      if (settings.imagem) {
        const imagemValue = settings.imagem.value || "";
        if (imagemValue) {
          if (imagemValue.startsWith('data:')) {
            const base64Pure = imagemValue.split(',')[1];
            setImagemPersona(base64Pure);
            setImagemPreview(imagemValue);
          } else {
            setImagemPersona(imagemValue);
            setImagemPreview(`data:image/jpeg;base64,${imagemValue}`);
          }
        }
      }
    } catch (error) {
      const message = error?.message || 'Erro ao carregar dados';
      setLoadingError(message);
    } finally {
      setLoading(false);
      setIsDirty(false); // Após carregar, sem alterações pendentes
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    let timer;
    if (showProgressModal && cacheClearStatus.phase === 'success') {
      if (countdown > 0) {
        timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      } else {
        setShowProgressModal(false);
        setCacheClearStatus({ phase: 'idle', message: '' });
      }
    }
    return () => clearTimeout(timer);
  }, [showProgressModal, cacheClearStatus.phase, countdown]);

  const handleAtivarIA = async () => {
    try {
      await apiService.request('/ia/status', {
        method: 'PUT',
        body: JSON.stringify({ status: true })
      });
      setIaLigada(true);
      addToastNotification({ id: Date.now(), title: "Sucesso", message: "IA ativada com sucesso!", type: "success" });
    } catch (error) {
      addToastNotification({ id: Date.now(), title: "Erro", message: "Erro ao ativar IA", type: "error" });
    }
  };

  const handleDesligarIA = async () => {
    try {
      await apiService.request('/ia/status', {
        method: 'PUT',
        body: JSON.stringify({ status: false })
      });
      setIaLigada(false);
      addToastNotification({ id: Date.now(), title: "Sucesso", message: "IA desativada com sucesso!", type: "success" });
    } catch (error) {
      addToastNotification({ id: Date.now(), title: "Erro", message: "Erro ao desligar IA", type: "error" });
    }
  };

  const handleLimparCache = () => {
    setShowConfirmModal(true);
  };

  const confirmarLimpezaCache = async () => {
    setShowConfirmModal(false);
    setShowProgressModal(true);
    setCacheClearStatus({ phase: 'loading', message: 'Limpando cache...' });
    try {
      await apiService.request('/ia/clear-cache', { method: 'DELETE' });
      setCacheClearStatus({ phase: 'success', message: 'Cache limpo com sucesso.' });
      setCountdown(3);
    } catch (error) {
      setCacheClearStatus({ phase: 'error', message: 'Erro de conexão ao limpar cache' });
    }
  };

  const cancelarLimpezaCache = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowConfirmModal(false);
      setIsClosingModal(false);
    }, 300);
  };

  const handleRetryLoad = () => {
    setLoadingError(null);
    setLoading(true);
    carregarDados();
  };

  const handleImagemUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        addToastNotification({ id: Date.now(), title: "Erro", message: 'Por favor, selecione apenas arquivos de imagem.', type: "error" });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        addToastNotification({ id: Date.now(), title: "Erro", message: 'A imagem deve ter no máximo 5MB.', type: "error" });
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64WithPrefix = e.target.result;
        const base64Pure = base64WithPrefix.split(',')[1];
        setImagemPersona(base64Pure);
        setImagemPreview(base64WithPrefix);
        setIsDirty(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoverImagem = () => {
    setImagemPersona('');
    setImagemPreview(null);
    setIsDirty(true);
    const fileInput = document.getElementById('imagemPersona');
    if (fileInput) fileInput.value = '';
  };

  const handleSalvarConfiguracoes = async () => {
    try {
      const configuracoes = [
        { key: "nome", value: nomePersona },
        { key: "descricao", value: descricaoPersona },
        { key: "script_recepcao", value: scriptRecepcao },
        { key: "imagem", value: imagemPersona },
      ];
      for (const config of configuracoes) {
        await apiService.request(`/ia/settings/${config.key}`, {
          method: 'PUT',
          body: JSON.stringify({ setting_value: config.value })
        });
      }
      addToastNotification({ id: Date.now(), title: "Sucesso", message: "Configurações da persona salvas com sucesso!", type: "success" });
      setIsDirty(false); // Limpa o estado após salvar
    } catch (error) {
      addToastNotification({ id: Date.now(), title: "Erro", message: `Erro ao salvar configurações: ${error.message}`, type: "error" });
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="w-full h-full flex items-center justify-center"
      >
        <LoadingSpinner size="lg" />
      </motion.div>
    );
  }

  if (loadingError) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, ease: "easeOut" }} className="w-full h-full">
        <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 sm:space-y-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-1">
              <h2 className="text-2xl sm:text-4xl font-extrabold font-headline tracking-tight text-on-surface">Inteligência Artificial</h2>
              <p className="text-on-surface-variant font-medium font-body">Não foi possível carregar as configurações.</p>
            </div>
          </div>
          <div className="bg-surface-container rounded-xl border border-outline-variant/10 shadow-xl p-8 flex flex-col items-center justify-center gap-4">
            <p className="text-sm text-error">{loadingError}</p>
            <button
              onClick={handleRetryLoad}
              className="px-6 py-3 rounded-xl bg-gradient-to-br from-primary to-primary-container text-on-primary font-bold hover:brightness-110 transition-all"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, ease: "easeOut" }} className="w-full h-full">
      <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 sm:space-y-8">
        <div>

          {/* Page Title */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-6 sm:mb-8">
            <div className="space-y-1">
              <h2 className="text-2xl sm:text-4xl font-extrabold font-headline tracking-tight text-on-surface">Inteligência Artificial</h2>
              <p className="text-on-surface-variant font-medium font-body">Configure e gerencie sua IA personalizada</p>
            </div>
          </div>

          {/* Top Grid: Status + Cache */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">

            {/* Status da IA */}
            <div className="bg-surface-container glow-emerald rounded-xl border border-outline-variant/10 shadow-xl relative overflow-hidden p-6 flex flex-col justify-between">
              <div className="absolute top-0 right-0 p-4 opacity-20">
                <span className="material-symbols-outlined" style={{ fontSize: '8rem' }}>settings_input_component</span>
              </div>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold font-headline text-on-surface mb-1">Status da IA</h3>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${iaLigada ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`}></span>
                    <span className={`font-medium text-sm ${iaLigada ? 'text-emerald-400' : 'text-red-400'}`}>
                      {iaLigada ? 'Sistema Ativo' : 'Sistema Inativo'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={handleAtivarIA}
                  disabled={iaLigada}
                  className={`flex items-center justify-center gap-2 py-4 px-6 rounded-xl font-bold transition-all ${
                    iaLigada
                      ? 'border-2 border-zinc-700 text-zinc-500 cursor-not-allowed'
                      : 'bg-gradient-to-br from-primary to-primary-container text-on-primary hover:brightness-110 active:scale-95'
                  }`}
                >
                  <span className="material-symbols-outlined">play_arrow</span>
                  Ativar IA
                </button>
                <button
                  onClick={handleDesligarIA}
                  disabled={!iaLigada}
                  className={`flex items-center justify-center gap-2 py-4 px-6 rounded-xl font-bold transition-all ${
                    !iaLigada
                      ? 'border-2 border-zinc-700 text-zinc-500 cursor-not-allowed'
                      : 'bg-error-container text-on-error-container hover:brightness-110 active:scale-95'
                  }`}
                >
                  <span className="material-symbols-outlined">pause</span>
                  Pausar IA
                </button>
              </div>
            </div>

            {/* Gerenciar Cache */}
            <div className="bg-surface-container glow-emerald rounded-xl border border-outline-variant/10 shadow-xl relative overflow-hidden p-6 flex flex-col justify-between">
              <div className="absolute top-0 right-0 p-4 opacity-20">
                <span className="material-symbols-outlined" style={{ fontSize: '8rem' }}>history</span>
              </div>
              <div className="mb-4">
                <h3 className="text-xl font-bold font-headline text-on-surface mb-2">Gerenciar Cache</h3>
                <p className="text-on-surface-variant text-sm leading-relaxed">
                  Limpar o cache removerá o contexto atual das conversas, permitindo que a IA reinicie diálogos sem interferência de interações passadas.
                </p>
              </div>
              <button
                onClick={handleLimparCache}
                className="w-full flex items-center justify-center gap-2 py-4 px-6 rounded-xl bg-tertiary-container text-on-tertiary-container font-bold hover:brightness-110 active:scale-95 transition-all shadow-lg"
              >
                <span className="material-symbols-outlined">delete_sweep</span>
                Limpar Cache
              </button>
            </div>
          </div>

          {/* Persona Form */}
          <div className="bg-surface-container glow-emerald rounded-xl border border-outline-variant/10 shadow-2xl overflow-hidden">
            <div className="p-8 border-b border-outline-variant/10 flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>psychology</span>
              </div>
              <div>
                <h3 className="text-xl font-bold font-headline text-on-surface">Persona da IA</h3>
                <p className="text-xs text-on-surface-variant uppercase tracking-widest font-bold">Personalização do Assistente</p>
              </div>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">

                {/* Left Column: Name + Image */}
                <div className="space-y-8">
                  <div className="space-y-2 flex flex-col">
                    <label className="text-sm font-semibold text-on-surface-variant ml-1">Nome da Persona</label>
                    <input
                      type="text"
                      value={nomePersona}
                      onChange={(e) => { setNomePersona(e.target.value); setIsDirty(true); }}
                      placeholder="Ex: Joana"
                      className="w-full bg-surface-container-highest border-b-2 border-outline-variant focus:border-primary border-t-0 border-x-0 rounded-t-lg px-4 py-3 focus:ring-0 transition-all text-on-surface font-medium outline-none"
                    />
                  </div>

                  <div className="space-y-4">
                    <label className="text-sm font-semibold text-on-surface-variant ml-1">Imagem de Introdução</label>
                    <div className="flex items-end gap-6">
                      <div className="relative w-48 h-48 rounded-xl overflow-hidden border-2 border-outline-variant/10 shadow-inner flex items-center justify-center bg-surface-container-highest">
                        {imagemPreview ? (
                          <>
                            <img
                              src={imagemPreview}
                              alt="Preview da persona"
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={handleRemoverImagem}
                              className="absolute top-2 right-2 bg-black/60 backdrop-blur-md p-1.5 rounded-full text-white hover:bg-red-500 transition-colors"
                            >
                              <span className="material-symbols-outlined text-sm">close</span>
                            </button>
                          </>
                        ) : (
                          <span className="material-symbols-outlined text-on-surface-variant" style={{ fontSize: '4rem' }}>image</span>
                        )}
                      </div>
                      <div>
                        <input
                          id="imagemPersona"
                          type="file"
                          accept="image/*"
                          onChange={handleImagemUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="imagemPersona"
                          className="cursor-pointer flex items-center gap-2 py-3 px-6 rounded-xl bg-primary/10 border border-primary/30 text-primary font-bold hover:bg-primary/20 transition-all active:scale-95"
                        >
                          <span className="material-symbols-outlined">image</span>
                          {imagemPreview ? 'Alterar Imagem' : 'Selecionar Imagem'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Description + Script */}
                <div className="space-y-8">
                  <div className="space-y-2 flex flex-col">
                    <label className="text-sm font-semibold text-on-surface-variant ml-1">Descrição da Persona</label>
                    <input
                      type="text"
                      value={descricaoPersona}
                      onChange={(e) => { setDescricaoPersona(e.target.value); setIsDirty(true); }}
                      placeholder="Ex: Atendente da Casa do Sushi"
                      className="w-full bg-surface-container-highest border-b-2 border-outline-variant focus:border-primary border-t-0 border-x-0 rounded-t-lg px-4 py-3 focus:ring-0 transition-all text-on-surface font-medium outline-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-on-surface-variant ml-1">Script de Recepção</label>
                    <textarea
                      value={scriptRecepcao}
                      onChange={(e) => { setScriptRecepcao(e.target.value); setIsDirty(true); }}
                      placeholder="Digite o script de recepção da IA..."
                      rows={8}
                      className="w-full bg-surface-container-highest border-b-2 border-outline-variant focus:border-primary border-t-0 border-x-0 rounded-t-lg px-4 py-4 focus:ring-0 transition-all text-on-surface font-medium leading-relaxed outline-none resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="pt-8 border-t border-outline-variant/10 flex justify-end">
                <button
                  onClick={handleSalvarConfiguracoes}
                  className="flex items-center gap-3 py-4 px-10 rounded-xl bg-gradient-to-br from-primary to-primary-container text-on-primary font-black shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all"
                  type="button"
                >
                  <span className="material-symbols-outlined">magic_button</span>
                  Salvar Configurações
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Confirm Cache Clear Modal */}
      {showConfirmModal && (
        <div
          className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 transition-all duration-300 ${
            isClosingModal ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <div
            className={`relative bg-surface-container rounded-2xl p-8 max-w-md w-full shadow-2xl border border-outline-variant/10 transform transition-all duration-300 ${
              isClosingModal ? 'scale-95 opacity-0 translate-y-4' : 'scale-100 opacity-100 translate-y-0'
            }`}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-error-container rounded-xl">
                <span className="material-symbols-outlined text-on-error-container">delete_sweep</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-on-surface">Confirmar Limpeza do Cache</h3>
                <p className="text-sm text-on-surface-variant">Esta ação não pode ser desfeita</p>
              </div>
            </div>
            <div className="mb-8">
              <div className="p-4 bg-error/10 rounded-xl border border-error/20 mb-4">
                <p className="text-on-surface-variant leading-relaxed">
                  Tem certeza que deseja limpar o cache? Todos os dados do histórico de conversas serão removidos permanentemente.
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-on-surface-variant">
                <span className="w-2 h-2 bg-error rounded-full animate-pulse"></span>
                <span>Ação irreversível</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={cancelarLimpezaCache}
                className="flex-1 px-6 py-3 text-sm font-semibold text-on-surface-variant bg-surface-container-highest border border-outline-variant/10 rounded-xl hover:brightness-110 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarLimpezaCache}
                className="flex-1 px-6 py-3 text-sm font-semibold bg-error-container text-on-error-container rounded-xl hover:brightness-110 transition-all"
              >
                Sim, Limpar Cache
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Modal */}
      {showProgressModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="relative bg-surface-container rounded-2xl p-8 max-w-md w-full shadow-2xl border border-outline-variant/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                {cacheClearStatus.phase === 'loading' && <Loader2 className="h-6 w-6 text-primary animate-spin" />}
                {cacheClearStatus.phase === 'success' && <span className="material-symbols-outlined text-primary">check_circle</span>}
                {cacheClearStatus.phase === 'error' && <X className="h-6 w-6 text-error" />}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-on-surface">
                  {cacheClearStatus.phase === 'loading' && 'Limpando cache'}
                  {cacheClearStatus.phase === 'success' && 'Cache limpo'}
                  {cacheClearStatus.phase === 'error' && 'Falha ao limpar cache'}
                </h3>
                <p className="text-sm text-on-surface-variant">{cacheClearStatus.message}</p>
              </div>
            </div>
            <div className="mb-6">
              {cacheClearStatus.phase === 'loading' && (
                <div className="w-full h-2 rounded-full bg-surface-container-highest overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary to-primary-container animate-pulse w-2/5"></div>
                </div>
              )}
              {cacheClearStatus.phase === 'success' && (
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 text-primary text-sm">
                    Cache da IA foi limpo e o contexto foi reiniciado.
                  </div>
                  <div className="flex items-center gap-2 text-sm text-on-surface-variant justify-center pt-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span>Fechando automaticamente em {countdown}...</span>
                  </div>
                </div>
              )}
              {cacheClearStatus.phase === 'error' && (
                <div className="p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm">
                  Verifique sua conexão e tente novamente.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              {(cacheClearStatus.phase === 'error' || cacheClearStatus.phase === 'success') && (
                <button
                  onClick={() => { setShowProgressModal(false); setCacheClearStatus({ phase: 'idle', message: '' }); }}
                  className="px-5 py-2 text-sm font-semibold text-on-primary bg-primary hover:brightness-110 rounded-xl shadow-md transition-all"
                >
                  Fechar
                </button>
              )}
              {cacheClearStatus.phase === 'loading' && (
                <button
                  disabled
                  className="px-5 py-2 text-sm font-semibold text-on-surface-variant bg-surface-container-highest rounded-xl cursor-not-allowed"
                >
                  Processando...
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Alterações Não Salvas — renderizado no body via Portal */}
      {showUnsavedModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleCancelLeave}
          />
          <div
            className="relative bg-surface-container rounded-2xl border border-white/10 shadow-2xl max-w-md w-full p-0 overflow-hidden"
            style={{ animation: "modalPop 0.2s cubic-bezier(0.34,1.56,0.64,1) both" }}
          >
            <div className="h-1.5 w-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500" />
            <div className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                  <span
                    className="material-symbols-outlined text-amber-400 text-2xl"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    warning
                  </span>
                </div>
                <div>
                  <h3 className="font-headline text-lg font-bold text-on-surface mb-1">
                    Alterações não salvas
                  </h3>
                  <p className="text-sm text-on-surface-variant leading-relaxed">
                    Você fez alterações na persona da IA que ainda{" "}
                    <strong className="text-on-surface">não foram salvas</strong>. Se sair agora, todas as modificações serão perdidas.
                  </p>
                </div>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 mb-6 flex items-center gap-2">
                <span className="material-symbols-outlined text-amber-400 text-base flex-shrink-0">info</span>
                <p className="text-xs text-amber-300/90">
                  Clique em <strong>"Continuar aqui"</strong> para voltar e salvar, ou em{" "}
                  <strong>"Sair sem salvar"</strong> para descartar.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleCancelLeave}
                  className="flex-1 bg-primary text-on-primary hover:bg-primary/90 rounded-xl px-5 py-3 font-semibold text-sm transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
                >
                  <span className="material-symbols-outlined text-base">arrow_back</span>
                  Continuar aqui
                </button>
                <button
                  onClick={handleConfirmLeave}
                  className="flex-1 bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest rounded-xl px-5 py-3 font-semibold text-sm transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-base">logout</span>
                  Sair sem salvar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes modalPop {
          from { opacity: 0; transform: scale(0.92) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </motion.div>
  );
};

export default IA;
